import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import GlobalSearchModal from "@/components/GlobalSearchModal";

/**
 * AppShell · layout principal grabarte 2026 (Mockup D #s1).
 *
 * Sidebar lateral oscuro persistente (240 px ▸ 64 px colapsable) con 3 grupos:
 *   · Catálogo  — Pizarra · Inicio · proyectos abiertos · "+ Nuevo proyecto"
 *   · Equipo    — Inbox · Bitácora
 *   · Compartido — Biblioteca · Ayudas · Admin
 *
 * Pie con avatar del usuario.
 *
 * NO se aplica a `/project/:id` (esas rutas usan `ProjectShell`).
 * Tampoco a `/login` y `/reset-password`.
 */

const COLLAPSE_KEY = "shell:nav-collapsed";

export default function AppShell({ children, active, onOpenSearch }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch (_e) { return false; }
  });
  const [projects, setProjects] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);

  // Atajo ⌘K / Ctrl+K para abrir el buscador desde cualquier sitio.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Carga ligera de proyectos para mostrarlos en el sidebar (Mockup D).
  useEffect(() => {
    let cancel = false;
    api.get("/icaa/projects").then((r) => {
      if (!cancel) setProjects(r.data || []);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [location.pathname]);

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  const isActive = (key) => active === key
    || (key === "projects" && location.pathname === "/")
    || (key === "library" && location.pathname.startsWith("/library"))
    || (key === "funding" && location.pathname.startsWith("/funding"))
    || (key === "manual" && location.pathname.startsWith("/manual"))
    || (key === "admin" && location.pathname.startsWith("/admin"));

  const initials = (user?.name || user?.email || "?").split(" ").filter(Boolean)
    .slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-gb-bg text-gb-black">
      {/* SIDEBAR oscuro */}
      <aside
        data-testid="app-sidebar"
        className={`sticky top-0 flex h-screen shrink-0 flex-col bg-gb-black text-white/80 transition-all duration-200 ${
          collapsed ? "w-[64px]" : "w-[240px]"
        }`}
      >
        {/* Head */}
        <div className={`relative flex items-center border-b border-white/8 py-4 ${collapsed ? "flex-col gap-2 px-2" : "gap-2 px-4"}`}>
          <Link to="/" className="group flex items-center gap-2 min-w-0" data-testid="shell-brand">
            <img src={collapsed ? "/brand/isotype-white.png" : "/brand/logo-white.png"} alt="grabarte"
                 className={`shrink-0 transition-all duration-200 group-hover:scale-110 ${collapsed ? "h-9 w-auto" : "h-7 w-auto"}`}
                 onError={(e) => { e.target.style.display = "none"; }} />
          </Link>
          <button
            onClick={toggle}
            data-testid="shell-toggle"
            className={`rounded text-white/40 transition hover:text-white ${collapsed ? "h-5 w-5 text-xs" : "ml-auto"}`}
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-2 py-3 text-sm">
          {/* Inicio · Diario */}
          <NavGroup label="Diario" collapsed={collapsed}>
            <NavLink to="/inicio" icon="☀" label="Inicio"
                     active={isActive("inicio")} collapsed={collapsed} testid="shell-nav-inicio" />
            <NavLink to="/tareas" icon="✓" label="Tareas"
                     active={isActive("tareas")} collapsed={collapsed} testid="shell-nav-tareas" />
          </NavGroup>

          {/* Catálogo */}
          <NavGroup label="Catálogo" collapsed={collapsed}>
            <NavLink
              to="/" icon="▦" label="Pizarra · Inicio"
              active={isActive("projects") && !location.pathname.startsWith("/project/")}
              collapsed={collapsed} testid="shell-nav-projects"
            />
            {projects.slice(0, 8).map((p) => {
              const isOpen = location.pathname === `/project/${p.id}`;
              return (
                <NavLink key={p.id}
                  to={`/project/${p.id}`}
                  icon={isOpen ? "▾" : "▸"}
                  label={p.title}
                  active={isOpen}
                  collapsed={collapsed}
                  testid={`shell-nav-project-${p.id}`}
                  faded={!isOpen}
                />
              );
            })}
            <NavLink to="/" icon="+" label="Nuevo proyecto" muted collapsed={collapsed}
                     onClick={(e) => { e.preventDefault(); navigate("/?new=1"); }}
                     testid="shell-new-project" />
          </NavGroup>

          {/* Compartido */}
          <NavGroup label="Compartido" collapsed={collapsed}>
            <NavLink to="/library" icon="▤" label="Biblioteca"
                     active={isActive("library")} collapsed={collapsed} testid="shell-nav-library" />
            <NavLink to="/funding" icon="⌑" label="Ayudas abiertas"
                     active={isActive("funding")} collapsed={collapsed} testid="shell-nav-funding" />
            <NavLink to="/festivals" icon="◈" label="Festivales"
                     active={isActive("festivals")} collapsed={collapsed} testid="shell-nav-festivals" />
            <NavLink to="/contactos" icon="◉" label="Contactos"
                     active={isActive("contactos")} collapsed={collapsed} testid="shell-nav-contactos" />
            <NavLink to="/manual" icon="❡" label="Manual"
                     active={isActive("manual")} collapsed={collapsed} testid="shell-nav-manual" />
            {user?.role === "admin" && (
              <NavLink to="/admin/users" icon="◷" label="Admin"
                       active={isActive("admin")} collapsed={collapsed} testid="shell-nav-admin" />
            )}
          </NavGroup>
        </div>

        {/* Footer con buscador + usuario */}
        <div className="border-t border-white/8 px-3 py-3">
          <button
            onClick={() => { setSearchOpen(true); onOpenSearch?.(); }}
            data-testid="shell-search-btn"
            title="Buscar (⌘K)"
            className={`mb-3 flex w-full items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2 py-2 text-left text-xs text-white/55 transition hover:border-white/25 hover:text-white ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <span>⌕</span>
            {!collapsed && (
              <>
                <span className="flex-1 truncate">Buscar</span>
                <kbd className="font-mono text-[10px] opacity-60">⌘K</kbd>
              </>
            )}
          </button>
          {user && (
            <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gb-red text-[11px] font-bold text-white">
                {initials}
              </span>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white" data-testid="shell-user-name">
                    {user.name?.split(" ")[0] || user.email?.split("@")[0]}
                  </p>
                  <button
                    onClick={logout}
                    data-testid="shell-logout"
                    className="text-[10px] uppercase tracking-widest text-white/40 transition hover:text-gb-red"
                  >
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <main className="min-w-0 flex-1">{children}</main>

      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

const NavGroup = ({ label, collapsed, children }) => (
  <div className="mb-4">
    {!collapsed && (
      <div className="mb-2 px-3 text-[9px] font-semibold uppercase tracking-[0.22em] text-white/35">
        {label}
      </div>
    )}
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

const NavLink = ({ to, icon, label, active, collapsed, muted, faded, badge, onClick, testid }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testid}
    title={collapsed ? label : undefined}
    className={`flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] transition ${
      active
        ? "bg-gb-red font-medium text-white"
        : muted
          ? "text-white/35 hover:bg-white/[0.06] hover:text-white/70"
          : faded
            ? "text-white/55 hover:bg-white/[0.06] hover:text-white"
            : "text-white/78 hover:bg-white/[0.06] hover:text-white"
    } ${collapsed ? "justify-center" : ""}`}
  >
    <span className="w-4 shrink-0 text-center text-[12px]">{icon}</span>
    {!collapsed && <span className="flex-1 truncate">{label}</span>}
    {!collapsed && badge && (
      <span className="rounded-full bg-white/12 px-1.5 py-0.5 text-[10px] font-semibold text-white/70">
        {badge}
      </span>
    )}
  </Link>
);
